import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// Material UI imports
import { Tooltip, useTheme } from "@mui/material";
import { Circle } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
// Custom components
import CustomAutocomplete from "components/Autocomplete";
// API calls
import { getAcademicYear } from "utils/requests/schoolPlan/academicYear";

// Checks if an academic year is the active one
export const isActiveAcademicYear = (academicYear) => {
  if (!academicYear) return false;
  if (typeof academicYear.status === "string") {
    return academicYear.status.toLowerCase() === "active";
  }
  return academicYear.is_active === true;
};

// Finds the active academic year in a list
export const findCurrentAcademicYear = (academicYears) =>
  academicYears.find((academicYear) => isActiveAcademicYear(academicYear)) ?? null;

/**
 * SelectAcademicYear renders a dropdown of the school's academic years and reports the
 * selected academic year id. The active year is selected by default and marked with a
 * green dot and tooltip in the dropdown.
 * @param {string} value - The currently selected academic year id
 * @param {Function} onChange - Callback receiving (academicYearId, academicYear) on selection
 * @param {string} label - Label for the input field
 * @param {string} placeholder - Placeholder text for the input field
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {boolean} required - Whether the field is marked as required
 * @param {boolean} error - Whether the field is in an error state
 * @param {string} helperText - Helper or error text shown below the input
 * @param {boolean} disableClearable - Whether the clear button is hidden
 * @param {boolean} defaultToCurrent - Selects the active academic year when no value is provided
 * @param {Array} academicYears - Pre-fetched options ({ id, label }) that skip the internal fetch
 * @param {string} size - Size of the input field
 * @param {Object} sx - Additional styles applied to the root component
 * @returns {JSX.Element} The rendered academic year selector
 * @example
 * <SelectAcademicYear
 *   value={academicYearId}
 *   onChange={(id) => setAcademicYearId(id)}
 * />
 */
const SelectAcademicYear = ({
  value,
  onChange,
  label = "Academic Year",
  placeholder = "Select academic year",
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  disableClearable = false,
  defaultToCurrent = true,
  academicYears = null,
  size = "medium",
  sx = {},
}) => {
  const isControlledOptions = Array.isArray(academicYears);
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [currentAcademicYearId, setCurrentAcademicYearId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const hasAppliedDefault = useRef(false);
  // Refs keep the fetch callback stable
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  valueRef.current = value;
  onChangeRef.current = onChange;

  const fetchAcademicYears = useCallback(async () => {
    setLoading(true);
    setFetchError("");

    try {
      const response = await getAcademicYear();

      if (response.success) {
        const fetchedAcademicYears = response.data?.academic_years ?? [];
        setOptions(
          fetchedAcademicYears.map((academicYear) => ({
            id: academicYear.academic_year_id,
            label: academicYear.label,
            calendar_type: academicYear.calendar_type,
          })),
        );

        const currentAcademicYear = findCurrentAcademicYear(fetchedAcademicYears);
        if (currentAcademicYear) {
          setCurrentAcademicYearId(currentAcademicYear.academic_year_id);
        }

        if (defaultToCurrent && !valueRef.current && !hasAppliedDefault.current) {
          if (currentAcademicYear) {
            hasAppliedDefault.current = true;
            onChangeRef.current(currentAcademicYear.academic_year_id, {
              id: currentAcademicYear.academic_year_id,
              label: currentAcademicYear.label,
              calendar_type: currentAcademicYear.calendar_type,
            });
          }
        }
      } else {
        setOptions([]);
        setFetchError(response.message || "Failed to load academic years.");
      }
    } catch (err) {
      setOptions([]);
      setFetchError("Something went wrong while loading the academic years.");
    } finally {
      setLoading(false);
    }
  }, [defaultToCurrent]);

  useEffect(() => {
    if (isControlledOptions) return;
    fetchAcademicYears();
  }, [fetchAcademicYears, isControlledOptions]);

  const resolvedOptions = isControlledOptions ? academicYears : options;

  // Parent options lack status, so fetch it to find and default to the active year
  useEffect(() => {
    if (!isControlledOptions) return;
    if (!academicYears || academicYears.length === 0) return;

    let isCancelled = false;

    const resolveCurrentFromProvidedOptions = async () => {
      const hasStatusInfo = academicYears.some(
        (option) => option?.status !== undefined || option?.is_active !== undefined,
      );
      let currentAcademicYear = null;

      if (hasStatusInfo) {
        currentAcademicYear = findCurrentAcademicYear(academicYears);
      } else {
        try {
          const response = await getAcademicYear();
          const fetchedAcademicYears = response.success ? response.data?.academic_years ?? [] : [];
          if (fetchedAcademicYears.length === 0) return;
          currentAcademicYear = findCurrentAcademicYear(fetchedAcademicYears);
        } catch (error) {
          // Options still work, only the default is skipped
          return;
        }
      }

      if (isCancelled || !currentAcademicYear) return;
      const currentId = currentAcademicYear.academic_year_id ?? currentAcademicYear.id;
      if (!currentId) return;
      setCurrentAcademicYearId(currentId);

      if (!defaultToCurrent || valueRef.current || hasAppliedDefault.current) return;
      const matchedOption = academicYears.find(
        (option) => (option.id ?? option.academic_year_id) === currentId,
      );
      if (!matchedOption) return;

      hasAppliedDefault.current = true;
      onChangeRef.current(currentId, {
        id: currentId,
        label: matchedOption.label ?? currentAcademicYear.label,
        calendar_type: matchedOption.calendar_type ?? currentAcademicYear.calendar_type,
      });
    };

    resolveCurrentFromProvidedOptions();

    return () => {
      isCancelled = true;
    };
  }, [isControlledOptions, defaultToCurrent, academicYears]);

  // The dropdown uses option objects, the consumer only the id
  const selectedOption = resolvedOptions.find((option) => option.id === value) ?? null;

  const handleChange = (event, newValue) => {
    onChange(newValue?.id ?? null, newValue ?? null);
  };

  // Renders an option with a green dot on the current academic year
  const renderAcademicYearOption = useCallback(
    (listProps, option) => {
      const optionId = option?.id ?? option?.academic_year_id;
      const isCurrent = Boolean(optionId) && optionId === currentAcademicYearId;
      const { key, ...optionProps } = listProps;
      return React.createElement(
        "li",
        { ...optionProps, key },
        <MDBox display="flex" alignItems="center" width="100%">
          <MDTypography variant="subtitle2" fontSize="0.9rem" sx={{ flex: 1 }}>
            {option?.label ?? ""}
          </MDTypography>
          {isCurrent && (
            <Tooltip title="Current Academic Year" placement="top" arrow>
              <Circle
                sx={{
                  ml: 1,
                  fontSize: 6,
                  color: theme.palette.success.main,
                  flexShrink: 0,
                  cursor: "default",
                }}
              />
            </Tooltip>
          )}
        </MDBox>,
      );
    },
    [currentAcademicYearId, theme],
  );

  return (
    <CustomAutocomplete
      options={resolvedOptions}
      getOptionLabel={(option) => option?.label ?? ""}
      getOptionValue={(option) => option?.id}
      isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
      renderOption={renderAcademicYearOption}
      value={selectedOption}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      loading={loading}
      loadingText="Loading academic years..."
      noOptionsText="No academic years available"
      disableClearable={disableClearable}
      size={size}
      error={error || Boolean(fetchError)}
      helperText={fetchError || helperText}
      sx={sx}
    />
  );
};

SelectAcademicYear.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disableClearable: PropTypes.bool,
  defaultToCurrent: PropTypes.bool,
  academicYears: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      calendar_type: PropTypes.string,
    }),
  ),
  size: PropTypes.oneOf(["small", "medium"]),
  sx: PropTypes.object,
};

export default SelectAcademicYear;
